// client/src/components/shared/FeaturedCarousel.js

import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getSecureImageUrl, handleImageError } from '../../utils/imageUtils';

/**
 * A lightweight, dependency-free horizontal carousel using CSS overflow and snap.
 * Shows a row of featured items (e.g., recently added), clickable to details.
 */
const FeaturedCarousel = ({
  items = [],
  getImageUrl = (item) => item.imageUrl || item.set_img_url,
  getItemName = (item) => item.name,
  getItemDetailUrl = (item) => '#',
  onItemClick = null,
  title = 'Featured',
}) => {
  const navigate = useNavigate();

  if (!items || items.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          px: 1,
          py: 1,
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 4 },
        }}
      >
        {items.map((item) => (
          <Card
            key={item._id}
            sx={{
              minWidth: 180,
              maxWidth: 220,
              scrollSnapAlign: 'start',
              cursor: 'pointer',
              flex: '0 0 auto',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
            onClick={() => onItemClick ? onItemClick(item) : navigate(getItemDetailUrl(item))}
          >
            <Box sx={{ pt: '56.25%', position: 'relative' }}>
              {getImageUrl(item) ? (
                <img
                  src={getSecureImageUrl(getImageUrl(item))}
                  alt={getItemName(item)}
                  loading="lazy"
                  onError={handleImageError}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '6px',
                  }}
                />
              ) : (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
                  <Typography variant="caption">No Image</Typography>
                </Box>
              )}
            </Box>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" noWrap>
                {getItemName(item)}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default FeaturedCarousel;
